import { useState } from "react";
import locationIllustration from "@/assets/froppio-location.svg";
import { LocationAddressFields } from "@/features/business/components/LocationAddressFields";
import { useCreateLocation, useUpdateLocation } from "@/features/business/hooks/businessLocationQueries";
import type { BusinessLocation } from "@/features/business/types/businessLocationTypes";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FormField } from "@/shared/components/ui/FormField";
import { FormPage } from "@/shared/components/ui/FormPage";
import { Section } from "@/shared/components/ui/Section";
import { SetupHeader } from "@/shared/components/ui/SetupHeader";

interface LocationOnboardingPageProps {
  businessId: string;
  location?: BusinessLocation;
  onSaved?: (location: BusinessLocation) => void;
}

export function LocationOnboardingPage({ businessId, location, onSaved }: LocationOnboardingPageProps) {
  const createLocation = useCreateLocation();
  const updateLocation = useUpdateLocation(location?.id ?? "");
  const mutation = location ? updateLocation : createLocation;
  const [formData, setFormData] = useState({
    name: location?.name ?? "",
    address: location?.address ?? "",
    city: location?.city ?? "",
    state: location?.state ?? "",
    postalCode: location?.postalCode ?? "",
  });

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (mutation.isPending || !businessId) return;
    const details = {
      name: formData.name.trim(),
      address: formData.address.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      postalCode: formData.postalCode.trim(),
    };
    if (location) {
      updateLocation.mutate(details, { onSuccess: onSaved });
    } else {
      createLocation.mutate({ ...details, businessId }, { onSuccess: onSaved });
    }
  };

  return (
    <div className="h-dvh">
      <FormPage
        header={<SetupHeader title="Where the magic happens" illustration={locationIllustration} step={3} stage="Location" />}
        onSubmit={handleSubmit}
        narrow
        actions={
          <Button type="submit" size="touch" disabled={mutation.isPending || !businessId}>
            {mutation.isPending ? "Saving..." : location ? "Save and review" : "Review setup"}
          </Button>
        }
      >
        <fieldset disabled={mutation.isPending} className="min-w-0 space-y-[26px]">
          <Section title="Name" card>
            <FormField id="location-name" label="Location name">
              <Input required pattern=".*\S.*" title="Enter a location name, not just spaces." value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value })} />
            </FormField>
          </Section>
          <Section title="Address" count="Optional" card>
            <LocationAddressFields value={formData} onChange={(address) => setFormData({ ...formData, ...address })} />
          </Section>
        </fieldset>
        {!businessId && <ErrorState message="A business is required before adding a location." />}
        {mutation.error && <ErrorState message={mutation.error.message || "We couldn't save your location. Please try again."} />}
      </FormPage>
    </div>
  );
}
